import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import {
  useSupportDiagnostics, collectClientSnapshot,
} from "@/hooks/useSupportDiagnostics";

const COPY = {
  en: {
    title: "Help & Support",
    sub: "Troubleshooting",
    explainer:
      "If something isn't working correctly, JobClose Support may ask you to enable diagnostics. Diagnostics include technical information about your JobClose session and device, but do not include your passwords or payment information. Diagnostics turn themselves off automatically after 60 minutes.",
    enable: "Enable Diagnostics",
    disable: "Disable Diagnostics",
    send: "Send Diagnostic Report",
    note_ph: "Briefly describe what happened (optional).",
    on: "Diagnostics ON",
    sending: "Sending…",
    success: (ref) =>
      `Diagnostic report sent. Reference #${ref}. JobClose Support will follow up.`,
    error: "Something went wrong sending the report. Please try again.",
  },
  es: {
    title: "Ayuda y Soporte",
    sub: "Solución de problemas",
    explainer:
      "Si algo no está funcionando correctamente, el Soporte de JobClose puede pedirte que actives el diagnóstico. El diagnóstico incluye información técnica sobre tu sesión y tu dispositivo, pero no incluye tus contraseñas ni datos de pago. El diagnóstico se apaga automáticamente después de 60 minutos.",
    enable: "Activar diagnóstico",
    disable: "Desactivar diagnóstico",
    send: "Enviar informe",
    note_ph: "Describe brevemente lo que sucedió (opcional).",
    on: "Diagnóstico ACTIVADO",
    sending: "Enviando…",
    success: (ref) =>
      `Informe enviado. Referencia #${ref}. El Soporte de JobClose se pondrá en contacto.`,
    error: "Ocurrió un error al enviar el informe. Intenta de nuevo.",
  },
};

export default function HelpAndSupport() {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang === "es" ? "es" : "en"];
  const { enabled, enable, disable } = useSupportDiagnostics();
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const dedupe = useMemo(() =>
    `sd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    [enabled]);
  const send = async () => {
    setSending(true); setError(null); setResult(null);
    try {
      const snapshot = collectClientSnapshot();
      const { data } = await api.post("/support/diagnostics", {
        client_dedupe_id: dedupe,
        contractor_note: note,
        snapshot,
      });
      setResult(data);
    } catch (e) {
      setError(c.error);
    } finally { setSending(false); }
  };
  return (
    <div data-testid="help-and-support-page"
         className="max-w-2xl mx-auto px-4 py-8">
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-2">
        {c.title}
      </div>
      <h1 data-testid="help-troubleshooting-title"
          className="text-3xl font-bold mb-4">{c.sub}</h1>
      <p className="text-base text-gray-700 leading-relaxed mb-6">
        {c.explainer}
      </p>
      {enabled ? (
        <div data-testid="diag-on-chip"
             className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-sm text-blue-700 mb-4">
          <span className="w-2 h-2 rounded-full bg-blue-600" /> {c.on}
        </div>
      ) : null}
      <div className="flex flex-col gap-3">
        {!enabled ? (
          <button
            data-testid="help-enable-diag-btn"
            onClick={enable}
            className="w-full sm:w-auto inline-flex items-center justify-center min-h-[44px] px-6 py-3 rounded-md bg-[#1652f0] hover:bg-[#0f3fbf] text-white font-semibold text-sm">
            {c.enable}
          </button>
        ) : (
          <>
            <textarea
              data-testid="help-note-input"
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={c.note_ph}
              maxLength={2000}
              className="w-full min-h-[96px] border border-gray-300 rounded-md p-3 text-base"
            />
            <button
              data-testid="help-send-diag-btn"
              disabled={sending}
              onClick={send}
              className="w-full sm:w-auto inline-flex items-center justify-center min-h-[44px] px-6 py-3 rounded-md bg-[#1652f0] hover:bg-[#0f3fbf] text-white font-semibold text-sm disabled:opacity-60">
              {sending ? c.sending : c.send}
            </button>
            <button
              data-testid="help-disable-diag-btn"
              onClick={disable}
              className="w-full sm:w-auto inline-flex items-center justify-center min-h-[44px] px-6 py-3 rounded-md border border-gray-300 text-gray-800 font-semibold text-sm">
              {c.disable}
            </button>
          </>
        )}
      </div>
      {result ? (
        <div data-testid="help-send-success"
             className="mt-6 p-4 rounded-md bg-green-50 border border-green-200 text-green-800">
          {c.success(result.reference)}
        </div>
      ) : null}
      {error ? (
        <div data-testid="help-send-error"
             className="mt-6 p-4 rounded-md bg-red-50 border border-red-200 text-red-800">
          {error}
        </div>
      ) : null}
    </div>
  );
}
